import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Button from 'ui/Button/Button';
import { ButtonTypeEnum } from 'ui/Button/Button';
import IconButtonSizeEnum from '@inter-ikea-kompis/component-icon-button/lib/enums/IconButtonSizeEnum';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import FilterButton from 'components/FiltersButton/FilterButton';
import { showMobileFiltersSheet } from 'ducks/mobile';
import { useAppDispatch } from 'ducks/hooks';
import ChevronLeftIcon from '@ingka/ssr-icon/paths/chevron-left';
import { capitalizeFirstLetter } from 'products';

const BackDrop = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
`;
const ScrollWrapper = styled.div`
  position: relative;
  overflow-y: auto;
  height: 100%;
`;

const HeaderWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const CenteredTitle = styled(Title)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

type HeaderProps = {
  title?: string;
  showFilterButton?: boolean;
  onDismiss: () => void;
};

const Header = ({ title, showFilterButton = false, onDismiss }: HeaderProps) => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  return (
    <HeaderWrapper>
      <Button
        iconOnly
        size={IconButtonSizeEnum.medium}
        type={ButtonTypeEnum.tertiary}
        ssrIcon={ChevronLeftIcon}
        onClick={onDismiss}
        dataTestId="mobile-drawer-back-btn"
        aria-label={formatMessage({ id: 'storageone.back' })}
      />
      <CenteredTitle type={ThemeFontStyleTypeEnum.headingSmall}>
        {capitalizeFirstLetter(title || '')}
      </CenteredTitle>
      <FilterButton
        onClick={() => dispatch(showMobileFiltersSheet(true))}
        hidden={!showFilterButton}
        inDrawer
      />
    </HeaderWrapper>
  );
};

type Props = {
  open: boolean;
  title?: string;
  showFilterButton?: boolean;
  onDismiss: () => void;
  children: React.ReactNode;
};

const MobileDrawer = ({
  open,
  title,
  showFilterButton,
  onDismiss,
  children,
  ...props
}: React.ComponentProps<typeof BottomSheet> & Props) => {
  return (
    <BottomSheet
      open={open}
      header={<Header title={title} showFilterButton={showFilterButton} onDismiss={onDismiss} />}
      sibling={open && <BackDrop onClick={onDismiss} />}
      defaultSnap={({ maxHeight }) => maxHeight * 0.88}
      snapPoints={({ maxHeight }) => {
        return [maxHeight * 0.45, maxHeight * 0.88];
      }}
      blocking={false}
      expandOnContentDrag={true}
      {...props}
    >
      <ScrollWrapper>{children}</ScrollWrapper>
    </BottomSheet>
  );
};

export default MobileDrawer;
